import { Image, ImageField, LayoutServicePageState, Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames/bind';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Loader, { LoaderVariant } from 'components/helpers/Loader/Loader';
import Icon from 'components/helpers/Icon';

import type { ComponentProps } from 'lib/component-props';
import compose from 'lib/enhancers/compose';
import withStyles from 'lib/enhancers/withStyles';
import type { ResponsiveImageFields } from 'models/responsive-image';

import styles from '../EventListing/EventListing.module.scss';
import { fixMediaImagePath, getCleanID, getLanguageBasedUrl } from 'lib/utils';
import { fetchEvents } from '../EventListing/EventListingUtils';
import { Filter, EventTypeData } from '../EventListing/Types/EventListing.Type';
import { EventListingResponse } from '../EventListing/Types/EventListingResponse.Type';
import Button, { ButtonVariant } from 'components/helpers/Button/Button';
import mockData from './MockData/mock.data.on-demand-event-listing.json';
import { useI18n } from 'next-localization';
import { removeFromFilter, useEventTypeFilter } from '../EventListing/EventListingUtils';
import { useRouter } from 'next/router';

type OnDemandEventListingFields = ResponsiveImageFields & {
  title: string;
  eventFallbackImage: ImageField;
  sortDescending: boolean;
  numberOfResultsToDisplay: number;
  featuredEventIds: string[];
  eventDetailPageUrl: string;
  audienceFilters: Audience[];
};

export type Audience = {
  id: string;
  name: string;
};

type OnDemandEventListingProps = ComponentProps & {
  fields: OnDemandEventListingFields;
};

const cx = classNames.bind(styles);

const OnDemandEventListing = (props: OnDemandEventListingProps): React.JSX.Element => {
  const { className, fields } = props;
  const [events, setEvents] = useState<EventListingResponse[]>([]);
  const { t } = useI18n();
  const { sitecoreContext } = useSitecoreContext();
  const [showNoResultsFound, setShowNoResultsFound] = useState<boolean>(false);
  const [showLoader, setShowLoader] = useState<boolean>(true);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [eventsOnPageLoad, setEventsOnPageLoad] = useState<EventListingResponse[]>([]);
  const hasFetchedOnload = useRef(false);
  const [shouldQuery, setShouldQuery] = useState<boolean>(true);

  //OnLoad Event Listing Fetch
  useEffect(() => {
    const isNormalPageMode = sitecoreContext.pageState === LayoutServicePageState.Normal;
    const shouldLoad = (!isNormalPageMode || shouldQuery) && !hasFetchedOnload.current;
    if (!shouldLoad) return;
    if (isNormalPageMode) {
      const dynamicFilters: Filter[] = [];
      const audienceFilters: Filter[] = [];
      const locationFilters: Filter[] = [
        { name: 'locationType', value: getCleanID('72E84054-9027-4AA3-8659-6430EAF72C68'), operator: 'CONTAINS' },
      ];
      const audience = fields.audienceFilters;

      audience?.forEach((type) => {
        const typeId = getCleanID(type?.id);
        if (typeId) {
          audienceFilters.push({ name: 'audienceSelected', value: typeId, operator: 'CONTAINS' });
        }
      });
      if (audienceFilters?.length) {
        dynamicFilters.push({ OR: audienceFilters });
      }
      if (locationFilters?.length) {
        dynamicFilters.push({ OR: locationFilters });
      }
      fetchEvents({
        language: sitecoreContext.language,
        dynamicFilters: dynamicFilters,
        onSuccess: (data) => {
          setEvents(data);
          setEventsOnPageLoad(data);
          setShowLoader(false);
          setShowNoResultsFound(data.length === 0);
          hasFetchedOnload.current = true;
        },
        onError: () => {
          setEvents([]);
          setShowLoader(false);
          setShowNoResultsFound(false);
          hasFetchedOnload.current = true;
        },
      });
    } else {
      setEvents(mockData as unknown as EventListingResponse[]);
      setEventsOnPageLoad(mockData as unknown as EventListingResponse[]);
      setShowLoader(false);
      setShowNoResultsFound((mockData as unknown as EventListingResponse[]).length === 0);
      hasFetchedOnload.current = true;
    }
  }, [fields, setEvents, sitecoreContext, shouldQuery]);

  const replaceToken = (text?: string): string => {
    const count = showNoResultsFound ? '0' : events.length?.toString();
    return text?.replace('{count}', count) ?? '';
  };

  const getUrlForEventId = (event: EventListingResponse) => {
    return getLanguageBasedUrl(`${props.fields.eventDetailPageUrl}?eventId=${event?.id}`, sitecoreContext.language);
  };

  const handleEventDetail = async (
    _e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    event: EventListingResponse
  ) => {
    window.location.href = getUrlForEventId(event);
    return false;
  };

  //Filters
  const getEventTypes = (eventsOnPageLoad: EventListingResponse[]): EventTypeData[] => {
    const eventTypeMap = new Map<string, EventTypeData>();

    eventsOnPageLoad.forEach((event) => {
      const typeId = event.type?.targetItem?.id;
      const typeName = event.type?.targetItem?.eventType?.value;
      if (typeName && typeId && !eventTypeMap.has(typeId)) {
        eventTypeMap.set(typeId, {
          id: typeId,
          name: String(typeName),
        });
      }
    });
    return Array.from(eventTypeMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  };

  const handleFilterChange = useCallback(
    (eventTypes: EventTypeData[]) => {
      const dynamicFilters: Filter[] = [];
      const audienceFilters: Filter[] = [];
      const typeFilters: Filter[] = [];
      const locationFilters: Filter[] = [
        { name: 'locationType', value: getCleanID('72E84054-9027-4AA3-8659-6430EAF72C68'), operator: 'CONTAINS' },
      ];
      const audience = fields.audienceFilters;

      audience?.forEach((type) => {
        const typeId = getCleanID(type?.id);
        if (typeId) {
          audienceFilters.push({ name: 'audienceSelected', value: typeId, operator: 'CONTAINS' });
        }
      });

      eventTypes?.forEach((type) => {
        const typeId = type?.id;
        if (typeId) {
          typeFilters.push({ name: 'type', value: typeId, operator: 'CONTAINS' });
        }
      });
      if (audienceFilters?.length) {
        dynamicFilters.push({ OR: audienceFilters });
      }
      if (typeFilters?.length) {
        dynamicFilters.push({ OR: typeFilters });
      }
      if (locationFilters?.length) {
        dynamicFilters.push({ OR: locationFilters });
      }
      fetchEvents({
        language: sitecoreContext.language,
        dynamicFilters: dynamicFilters,
        onSuccess: (data) => {
          setEvents(data);
          setShowLoader(false);
          setShowNoResultsFound(data.length === 0);
          setCurrentPageIndex(0);
          setShouldQuery(false);
        },
        onError: () => {
          setEvents([]);
          setShowLoader(false);
          setShowNoResultsFound(false);
        },
      });
    },
    [fields.audienceFilters, sitecoreContext.language]
  );

  const router = useRouter();
  const { updateFiltersInUrl } = useEventTypeFilter(router);
  const [filtersActive, setFiltersActive] = useState(false);
  const [filtersToShow, setFiltersToShow] = useState(5);
  const [viewAllTypes, setViewAllTypes] = useState(false);
  const eventTypes = useMemo(() => getEventTypes(eventsOnPageLoad), [eventsOnPageLoad]);
  const showViewMore = eventTypes.length > 5;
  const [selectedEventTypeFilter, setSelectedEventTypeFilter] = useState<EventTypeData[]>([]);
  const hasAppliedQueryEventTypes = useRef(false);

  useEffect(() => {
    if (
      hasAppliedQueryEventTypes.current ||
      !router.isReady ||
      sitecoreContext.pageState !== LayoutServicePageState.Normal ||
      !eventTypes.length
    )
      return;
    hasAppliedQueryEventTypes.current = true;
    if (router.asPath?.indexOf('?') > 0) {
      const queryEventTypes = router.query.eventType as string | undefined;

      const matchedEventTypes = queryEventTypes
        ? eventTypes.filter((et) => queryEventTypes.split(',').includes(et.id))
        : [];

      if (matchedEventTypes.length) {
        setSelectedEventTypeFilter(matchedEventTypes);
        handleFilterChange(matchedEventTypes);
      }
    }
  }, [router.isReady, router.asPath, router.query.eventType, sitecoreContext, eventTypes, handleFilterChange]);

  const onViewMoreClick = () => {
    const typesToShow = !viewAllTypes ? eventTypes.length : 5;
    setViewAllTypes(!viewAllTypes);
    setFiltersToShow(typesToShow);
  };

  const updateEventTypeFilters = (eventTypes: EventTypeData[]) => {
    handleFilterChange(eventTypes);
    updateFiltersInUrl(eventTypes, [], '');
  };

  const onClearFilters = () => {
    setSelectedEventTypeFilter([]);
    updateEventTypeFilters([]);
    updateFiltersInUrl([], [], '');
  };

  const onEventTypeClick = (type: EventTypeData) => {
    const exists = selectedEventTypeFilter.find((t) => t.id === type.id);
    const updatedEventTypes = exists
      ? selectedEventTypeFilter.filter((t) => t.id !== type.id)
      : [...selectedEventTypeFilter, type];
    setSelectedEventTypeFilter(updatedEventTypes);
    updateEventTypeFilters(updatedEventTypes);
  };

  //Pagination
  const paginationButtonClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    const target = e.target as HTMLButtonElement;
    const pageIndex = Number(target.dataset['pageIndex']);
    setCurrentPageIndex(pageIndex);
  };

  const previousButtonClick: React.MouseEventHandler<HTMLButtonElement> = () => {
    setCurrentPageIndex(currentPageIndex - 1);
  };

  const nextButtonClick: React.MouseEventHandler<HTMLButtonElement> = () => {
    setCurrentPageIndex(currentPageIndex + 1);
  };

  const pageSize = +(fields.numberOfResultsToDisplay || 10);
  const totalPages = Math.ceil(events?.length / pageSize);
  const startItemIndex = currentPageIndex === 0 ? currentPageIndex : currentPageIndex * pageSize;
  const endItemIndex = startItemIndex === 0 ? pageSize : startItemIndex + pageSize;

  return (
    <div className={cx('event-listing', 'component-container-edge', className)}>
      {showLoader && (
        <div role="status">
          <span className={cx('screen-reader-only')}>{t('Event listing loaded')}</span>
          <Loader variant={LoaderVariant.Spinner} />
        </div>
      )}
      {!showLoader && !(eventsOnPageLoad?.length > 0) && (
        <div role="status" className={cx('event-listing-no-filters')}>
          <div className={cx('header-row')}>
            <Text encode={false} field={{ value: replaceToken(fields.title) }} tag="h2" />
            <div role="status" aria-live="polite" aria-atomic="true">
              <span className={cx('screen-reader-only')}>{replaceToken(fields.title)}</span>
            </div>
          </div>
          <div className={cx('no-events')}>
            <div className={cx('icon-wrapper')}>
              <Icon variant="calendar-in-circle" />
            </div>
            <strong>{t('NoEventFound')}</strong>
          </div>
        </div>
      )}
      <div className={cx('event-listing-with-filters')}>
        {!showLoader && eventsOnPageLoad?.length > 0 && (
          <>
            <div className={cx('head-div', `head-div__active--${filtersActive}`)}>
              <div className={cx('header-row')}>
                <h2>{replaceToken(fields.title)}</h2>
                <div role="status" aria-live="polite" aria-atomic="true">
                  <span className={cx('screen-reader-only')}>{replaceToken(fields.title)}</span>
                </div>
              </div>
              <Button className={cx('filter-heading-mobile')} onClick={() => setFiltersActive(!filtersActive)}>
                {t('Filters')}
                <Icon variant={filtersActive ? 'close' : 'filter'} />
              </Button>
              {selectedEventTypeFilter.length > 0 && (
                <div className={cx('filter-chips')}>
                  {selectedEventTypeFilter?.map((type: EventTypeData, index: number) => (
                    <div key={index} className={cx('filter-chip')}>
                      <Button
                        onClick={() => removeFromFilter(`event-type-${type.id}`)}
                        aria-label={`Remove filter: ${type.name}`}
                      >
                        {type.name}
                        <Icon variant="close" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className={cx('filters', `filters__active--${filtersActive}`)}>
              <h2 className={cx('filter-heading-lg')}>{t('Filters')}</h2>
              <div className={cx('event-filters')}>
                {eventTypes?.length > 1 && (
                  <fieldset className={cx('type-filter')}>
                    <legend>{t('EventType')}</legend>
                    <ul>
                      {eventTypes.slice(0, filtersToShow).map((type, index) => (
                        <li key={`event-type-${index}`}>
                          <input
                            type="checkbox"
                            id={`event-type-${type.id}`}
                            checked={selectedEventTypeFilter.some((t) => t.id === type.id)}
                            onChange={() => onEventTypeClick(type)}
                          />
                          <label htmlFor={`event-type-${type.id}`}>{type.name}</label>
                        </li>
                      ))}
                    </ul>
                    {showViewMore && (
                      <a onClick={onViewMoreClick} className={cx('view-more-types')}>
                        {viewAllTypes ? t('ViewLess') : t('ViewMore')}
                        <Icon variant={viewAllTypes ? 'minus' : 'plus'} />
                      </a>
                    )}
                  </fieldset>
                )}

                {selectedEventTypeFilter.length > 0 && (
                  <div className={cx('clear-filters')}>
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        onClearFilters();
                      }}
                    >
                      {t('Clear Filters')}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {!showLoader && !showNoResultsFound && (
          <div className={cx('listing', 'event-div')}>
            {events?.slice(startItemIndex, endItemIndex.valueOf()).map((event) => {
              const isValidImage =
                event.image?.jsonValue?.value && Object.keys(event.image?.jsonValue?.value).length > 0;
              const eventImage = isValidImage ? event.image?.jsonValue : fixMediaImagePath(fields?.eventFallbackImage);
              const isFeatured = fields.featuredEventIds.some((featuredId) => getCleanID(featuredId) === event.id);
              return (
                <div key={event.id} className={cx('event-card')}>
                  <div className={cx('event-listing-image-column')}>
                    <Image field={eventImage} className={cx('event-listing-image-column__image')} />
                  </div>
                  <div className={cx('event-listing-content-column')}>
                    {isFeatured && <span className={cx('featured-tag')}>Featured Event</span>}
                    <a
                      href={getUrlForEventId(event)}
                      onClick={(e) => {
                        e.preventDefault();
                        handleEventDetail(e, event);
                      }}
                      className={cx('event-detail-link')}
                    >
                      <Text encode={sitecoreContext.pageEditing} field={event.name} tag="h3" />
                      <Icon variant="carat" />
                    </a>
                    <div className={cx('icon-wrapper')}>
                      <Icon variant="play" className={cx('event-icon')} /> <span>{t('AvailableOnDemand')} </span>
                    </div>
                    <Text tag="p" field={event.type?.targetItem?.eventType} className={cx('event-type')} />
                  </div>
                </div>
              );
            })}
            {totalPages > 1 && (
              <div className={cx('pagination-container')}>
                <Button
                  className={cx('previous-button')}
                  variant={ButtonVariant.Anchor}
                  disabled={currentPageIndex == 0}
                  onClick={previousButtonClick}
                >
                  <Icon variant="carat" /> {t('Previous')}
                </Button>
                <div className={cx('pagination-numeric-button')}>
                  {Array.from({ length: totalPages }, (_v, i) => (
                    <Button
                      variant={ButtonVariant.Anchor}
                      data-page-index={i}
                      key={`page-${i}`}
                      onClick={paginationButtonClick}
                    >
                      {i + 1}
                    </Button>
                  ))}
                </div>
                <Button
                  className={cx('next-button')}
                  variant={ButtonVariant.Anchor}
                  disabled={currentPageIndex >= totalPages - 1}
                  onClick={nextButtonClick}
                >
                  {t('Next')} <Icon variant="carat" />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default compose<OnDemandEventListingProps>(withStyles())(OnDemandEventListing);
